import {
  curry,
  compose,
  prop,
  defaultTo,
  path,
  pick,
  filter,
  keys,
  length,
  pathOr,
  split,
  propOr,
  map,
  not,
  startsWith,
  fromPairs,
  has,
  head,
  pipe,
  isEmpty,
  isNil
} from 'ramda'
import moment from 'moment'
import numberRound from 'utils/numberRound'
import { getSearchParam, parseParams } from './urls'
import { mapParamsToRequest, mapStrToBoolean, decodeURLParams } from './mapper'

export const getIdFromProps = curry((paramName, props) =>
  compose(
    parseInt,
    pathOr(0, ['match', 'params', paramName])
  )(props)
)

export const getParamsFormHistory = compose(
  parseParams,
  path(['location', 'search'])
)

export const getParamFromHistory = curry((key, history) =>
  compose(
    prop(key),
    parseParams,
    path(['location', 'search'])
  )(history)
)

export const getSelectedFromHistory = compose(
  map(Number),
  filter(
    compose(
      not,
      isNaN,
      parseInt
    )
  ),
  split('-'),
  propOr('', 'selected'),
  parseParams,
  path(['location', 'search'])
)
const orderingMapper = item => {
  if (startsWith('-', item)) {
    return [item.slice(1), 'asc']
  }
  return [item, 'desc']
}

export const getOrderingFromHistory = compose(
  fromPairs,
  map(orderingMapper),
  split(','),
  propOr('', 'ordering'),
  parseParams,
  path(['location', 'search'])
)

export const getParamsCountFromHistory = curry((fields, history) =>
  compose(
    length,
    keys,
    filter(Boolean),
    pick(fields),
    parseParams,
    path(['location', 'search'])
  )(history)
)

export const getInitValuesFromHistory = curry((fields, history) =>
  compose(
    filter(Boolean),
    pick(fields),
    parseParams,
    path(['location', 'search'])
  )(history)
)

export const getDataFromState = curry((name, state) => ({
  loading: path([name, 'loading'], state),
  failed: path([name, 'failed'], state),
  data: path([name, 'data'], state),
  results: pathOr([], [name, 'data', 'results'], state)
}))

export const getBooleanFromHistory = curry((name, history) =>
  compose(
    mapStrToBoolean,
    defaultTo('false'),
    prop(name),
    parseParams,
    path(['location', 'search'])
  )(history)
)

export const getListParamsFromProps = compose(
  mapParamsToRequest,
  decodeURLParams,
  getParamsFormHistory,
  prop('history')
)

export const get2D = num => {
  if (num.toString().length < 2) {
    return '0' + num
  }
  return num.toString()
}

export const getDuration = (seconds, placeholder, withSeconds) => {
  if (!seconds && placeholder) {
    return placeholder
  }
  if (!seconds) {
    return `00:00${withSeconds ? ':00' : ''}`
  }
  if (withSeconds) {
    const toHour = seconds / 3600
    const toMin = ((seconds / 3600) - Math.floor(seconds / 3600)) * 60
    const toSec = numberRound((toMin - Math.floor(toMin)) * 60) || 0

    const hour = get2D(Math.floor(toHour))
    const mins = get2D(Math.floor(toMin))
    const sec = get2D(toSec.toFixed(0))
    return `${hour}:${mins}:${sec}`
  }
  const mins = get2D(seconds % 60)
  const hour = get2D(Math.floor(seconds / 60))
  return `${hour}:${mins}`
}

export const getDurationInMinutes = (duration) => {
  return moment.duration(duration).asMinutes()
}
const isObject = obj => typeof obj === 'object'
const isNotObject = obj => typeof obj !== 'object'
const isNotEmptyObj = obj => isObject(obj) && !isEmpty(obj)
const filterArray = filter(item => isNotObject(item) || isNotEmptyObj(item))

export const getSerializedData = (fields, data) =>
  compose(
    fromPairs,
    map(key => {
      const defaultValue = prop(key, data)
      const isArray = Array.isArray(defaultValue)
      if (isArray) {
        const arrValue = filterArray(defaultValue)
        const hasId = pipe(
          head,
          defaultTo({}),
          has('_id')
        )(arrValue)
        if (hasId) return [key, arrValue.map(prop('_id'))]
        return [key, arrValue]
      }
      const values = pathOr(defaultValue, [key, '_id'], data)
      if (isNil(values)) {
        return [key, undefined]
      }
      return [key, values]
    })
  )(fields)

export const getIdForInitValues = (data, keys) => pipe(
  map(key => {
    const value = prop(key, data)
    if (isNil(value)) {
      return null
    } else if (isNaN(value)) {
      return [key, { id: value }]
    } else {
      return [key, { id: Number(value) }]
    }
  }),
  filter(pipe(isNil, not)),
  fromPairs
)(keys)

export const getNormalizedDuration = time =>
  (time && time.length === 5 && `${time}:00`) || time

const dateFormat = 'YYYY-MM-DD'

const defaultDate = moment().format(dateFormat)
export const getDateFromUrl = location => {
  return getSearchParam('date', path(['search'], location)) || defaultDate
}

export const getItemFromTree = (arr, target) => {
  for (const i in arr) {
    const a = arr[i]
    if (a.id === target) {
      return a
    }
    if (Array.isArray(a.children)) {
      const child = getItemFromTree(a.children, target)
      if (child !== null) {
        return child
      }
    }
  }
  return null
}

export const getPositionRate = pipe(
  map(rate => [`_${rate.position.id}`, rate.amountPerHour]),
  fromPairs
)

export const getFullName = (data) => {
  return `${propOr('', 'firstName', data)} ${propOr('', 'lastName', data)} ${propOr('', 'parentName', data)}`
}
